import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { getAppName } from '@rsos/utils/metaTags';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import { ChatContainer } from './TF911.styles';
import { TF911_CHAT_HEADER_HEIGHT } from './TF911ChatBox';

const feAppName = getAppName();

/**
 * Create a logged-in user object used for TalkJS authentication
 * @param {String} role - Name of role that maps to a corresponding TalkJS role
 */
const useTF911LoggedinUser = role => {
  const profile = useSelector(state => state.sinatra.user.profile);

  const currentPSAPDisplayName = useSelector(
    state => state.psaps.currentPSAP.display_name,
  );

  const loggedInUser = useMemo(() => {
    const {
      id = '',
      first_name = '',
      last_name = '',
      email = null,
      talkjs_signature = '',
    } = profile;

    return {
      id: parseInt(id),
      name: `${first_name} ${last_name}`,
      email: email,
      signature: talkjs_signature,
      custom: {
        org_name: currentPSAPDisplayName || '',
        feAppName,
      },
      ...(role ? { role } : {}),
    };
  }, [currentPSAPDisplayName, profile, role]);

  return loggedInUser;
};

/**
 * Create a TalkJSchat session for a given caller ID
 * @param {String} callerID - phone number
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured
 * messages that drives some UI updates
 * @param {Boolean} showStartChat - Flag that controls the height and max height
 * of the component
 * @param {Boolean} isTextFrom911ChatBoxVisible - Flag that controls overflowY
 * styling
 */
const TF911Chat = ({
  callerID,
  showPreconfiguredMessages,
  showStartChat,
  isTextFrom911ChatBoxVisible,
}) => {
  const chatRef = useRef();

  const [chatSession, setChatSession] = useState(null);
  const [chatbox, setChatbox] = useState(null);

  const uiTheme = useSelector(state => state.sinatra.ui.theme);

  const chatTheme = useMemo(
    () => (uiTheme === 'light' ? 'tf911_light' : 'tf911_dark'),

    [uiTheme],
  );

  const talkjsUserRole = useMemo(
    () =>
      uiTheme === 'light' ? 'rsp_tf911_light_user' : 'rsp_tf911_dark_user',
    [uiTheme],
  );

  const conversationID = useSelectedTextFrom911ConversationID(callerID);

  const conversation = useSelectedTextFrom911Conversation(callerID);

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const loggedInUser = useTF911LoggedinUser(talkjsUserRole);

  const chatHeight = useMemo(() => {
    if (
      conversationID &&
      isTextFrom911ChatBoxVisible &&
      !showPreconfiguredMessages
    ) {
      return '100%';
    }
    return '0';
  }, [conversationID, isTextFrom911ChatBoxVisible, showPreconfiguredMessages]);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type Response',
          },
        });

        chat.mount(chatRef.current);

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));

      chatbox.select(conversation);

      chatbox.messageField.setVisible(true);
    }
  }, [chatSession, chatbox, conversationID, loggedInUser]);

  useEffect(() => {
    if (chatbox && isConversationEnded) {
      chatbox.messageField.setVisible(false);
    }
  }, [chatbox, isConversationEnded]);

  return (
    <ChatContainer
      height={chatHeight}
      maxHeight={
        showStartChat && !conversationID
          ? 0
          : `calc(100% - ${TF911_CHAT_HEADER_HEIGHT}px)`
      }
      overflowY={!isTextFrom911ChatBoxVisible ? 'hidden' : 'auto'}
      showBorder={isTextFrom911ChatBoxVisible}
      ref={chatRef}
    />
  );
};
export default TF911Chat;
